import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Folders, ArrowClockwise, Warning, CheckSquareOffset, ArrowsLeftRight,
  PencilSimple, Check, X, CaretRight, Copy, Files, ArrowSquareOut, Scissors,
  ArrowUUpLeft } from '@phosphor-icons/react';
import { drawingRevApi, CONFIDENCE_META } from './drawingRevApi';
import { dmsApi } from './dmsApi';
import DrawingCompare from './DrawingCompare';
import SimilarDrawings from './SimilarDrawings';
import DocFileViewer from './DocFileViewer';

const ConfBadge = ({ level }) => {
  const meta = CONFIDENCE_META[level];
  if (!meta) return null;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1 py-px rounded border text-[9px] font-medium ${meta.cls}`}
      title={meta.label} data-testid={`conf-${level}`}>
      <span aria-hidden>{meta.mark}</span>{meta.label}
    </span>
  );
};

/** 抽出値をその場で直せるセル（手動編集可）。
 *  保存するのは ✓ と Enter だけ。Escape と ✕ は何も保存せずに閉じる。 */
const EditableField = ({ value, level, placeholder, onSave, testid, canEdit = true }) => {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || '');
  useEffect(() => { setV(value || ''); }, [value]);
  const cancel = () => { setV(value || ''); setEditing(false); };
  const commit = async () => { await onSave(v); setEditing(false); };
  if (!editing) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className={`font-mono text-[11px] font-semibold ${value ? 'text-zinc-900' : 'text-zinc-400'}`}>
          {value || placeholder}
        </span>
        <ConfBadge level={level} />
        {canEdit && (
          <button type="button" onClick={() => setEditing(true)} data-testid={`${testid}-edit`}
            className="text-zinc-400 hover:text-teal-700"><PencilSimple size={11} /></button>
        )}
        {canEdit && level === 'manual' && (
          <button type="button" onClick={() => onSave('')} data-testid={`${testid}-auto`}
            title="人が入れた値を消して、図面から読み取った値に戻す"
            className="text-[9px] text-zinc-400 underline hover:text-teal-700">自動に戻す</button>
        )}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input value={v} onChange={(e) => setV(e.target.value)} autoFocus
        data-testid={`${testid}-input`} placeholder={placeholder}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); cancel(); }
        }}
        className="w-28 border border-teal-500 rounded px-1 py-0.5 font-mono text-[11px] outline-none" />
      <button type="button" data-testid={`${testid}-save`} onClick={commit}
        className="text-teal-700 hover:text-teal-900"><Check size={13} /></button>
      <button type="button" onClick={cancel}
        data-testid={`${testid}-cancel`} className="text-zinc-400 hover:text-zinc-700"><X size={12} /></button>
    </span>
  );
};

/** 1ページ目のサムネイル。画面に入ってから読む（枚数が多い束で効く）。 */
const RevThumb = ({ documentId, onOpen }) => {
  const ref = useRef(null);
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let alive = true; let made = null;
    const io = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      try {
        const u = await drawingRevApi.pageImage(documentId, 0.5, { maxEdge: 600 });
        made = u;
        if (alive) setUrl(u); else URL.revokeObjectURL(u);
      } catch { if (alive) setErr(true); }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => { alive = false; io.disconnect(); if (made) URL.revokeObjectURL(made); };
  }, [documentId]);
  return (
    <button ref={ref} type="button" onClick={onOpen} title="プレビューを開く"
      data-testid={`rev-thumb-${documentId}`}
      className="w-12 h-9 shrink-0 rounded border border-zinc-200 bg-white overflow-hidden grid place-items-center hover:border-teal-500 transition-colors">
      {url ? <img src={url} alt="" className="w-full h-full object-cover" />
        : <span className="text-[9px] text-zinc-400">{err ? '—' : '…'}</span>}
    </button>
  );
};

/** 表題欄から読み取った項目。その場で直せる（直した値は読み直しても残る）。 */
const FieldsBar = ({ member, labels, order, onSave, canEdit }) => {
  const fields = member.fields || {};
  const shown = order.filter((k) => fields[k]?.value || (member.fields_manual || {})[k]);
  if (!shown.length) {
    return (
      <p className="px-3 pb-2 text-[10px] text-zinc-400" data-testid={`fields-none-${member.document_id}`}>
        表題欄の項目は読み取れませんでした（「図面を読み直す」で再解析されます）
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 pb-2 pl-10"
      data-testid={`fields-bar-${member.document_id}`}>
      {shown.map((k) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className="text-[10px] text-zinc-500">{labels[k] || k}</span>
          <EditableField value={fields[k]?.value} level={fields[k]?.confidence}
            placeholder="未読" testid={`field-${k}-${member.document_id}`}
            canEdit={canEdit}
            onSave={(v) => onSave(member.document_id, k, v)} />
        </span>
      ))}
    </div>
  );
};

const FamilyRow = ({ f, onOpen }) => (
  <button type="button" onClick={() => onOpen(f.drawing_no_key)}
    data-testid={`family-row-${f.drawing_no_key}`}
    className="w-full text-left px-3 py-2.5 border-b border-zinc-200 hover:bg-zinc-50 transition-colors flex items-center gap-3">
    <Folders size={16} weight="duotone" className="shrink-0 text-zinc-400" />
    <span className="font-mono text-[12px] font-semibold text-zinc-900 w-44 truncate">{f.drawing_no}</span>
    <span className="text-[11px] text-zinc-500 w-16">{f.revision_count}版</span>
    <span className="text-[11px] text-zinc-700 w-24">
      {f.latest_rev ? `REV ${f.latest_rev}` : '—'}
    </span>
    <span className="font-mono text-[11px] text-zinc-500 w-24">{f.latest_rev_date || '日付なし'}</span>
    {f.customer_drawing_no && (
      <span className="font-mono text-[10px] text-zinc-500 w-32 truncate"
        title={`客先図番 ${f.customer_drawing_no}`}
        data-testid={`family-customer-${f.drawing_no_key}`}>客先 {f.customer_drawing_no}</span>
    )}
    {f.latest_undecided ? (
      <span className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-300 text-amber-800 text-[9px] font-semibold"
        data-testid={`family-undecided-${f.drawing_no_key}`}>判定できません</span>
    ) : f.is_confirmed ? (
      <span className="px-1.5 py-0.5 rounded bg-teal-50 border border-teal-200 text-teal-700 text-[9px] font-semibold">確定済</span>
    ) : (
      <span className="px-1.5 py-0.5 rounded bg-zinc-100 border border-zinc-200 text-zinc-500 text-[9px]">自動判定</span>
    )}
    {f.duplicate_count > 0 && (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-50 border border-orange-300 text-orange-800 text-[9px] font-medium"
        title="同じ中身（または同じ図番・REV・改訂日）の図面です。版としては数えていません"
        data-testid={`family-dups-${f.drawing_no_key}`}>
        <Files size={10} weight="fill" />重複 {f.duplicate_count}件
      </span>
    )}
    {f.has_manual_split && (
      <span className="px-1.5 py-0.5 rounded bg-sky-50 border border-sky-200 text-sky-700 text-[9px]"
        data-testid={`family-manual-${f.drawing_no_key}`}>人が分けた</span>
    )}
    {f.suffix_rule === 'variant' ? (
      <span className="px-1.5 py-0.5 rounded bg-sky-50 border border-sky-200 text-sky-700 text-[9px]"
        title="末尾の記号は別図番として扱っています（読み直しても束ね直しません）">末尾=別図番</span>
    ) : f.grouped_by === 'suffix' && (
      <span className="px-1.5 py-0.5 rounded bg-zinc-50 border border-zinc-200 text-zinc-500 text-[9px]"
        title="図番の末尾に付いた版記号（例 TA-97015B / TA-97015C）で同じ図面としてまとめています">図番末尾で束ね</span>
    )}
    {f.confidence === 'uncertain' && (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 border border-amber-300 text-amber-800 text-[9px] font-medium">
        <Warning size={10} weight="fill" />推測で束ねています（{f.uncertain_members}枚）
      </span>
    )}
    {f.disagrees_with_auto && (
      <span className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-300 text-amber-800 text-[9px]">自動判定と相違</span>
    )}
    <CaretRight size={13} className="ml-auto shrink-0 text-zinc-300" />
  </button>
);

export default function DrawingRevisions() {
  const [list, setList] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [fam, setFam] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState([]);
  const [compare, setCompare] = useState(null);
  const [unassigned, setUnassigned] = useState(null);
  const [meta, setMeta] = useState({ labels: {}, order: [] });
  const [canEdit, setCanEdit] = useState(false);
  const [similarFor, setSimilarFor] = useState(null);
  const [preview, setPreview] = useState(null);
  const [moveFor, setMoveFor] = useState(null);   // {document_id, value}

  const loadList = useCallback(async () => {
    const d = await drawingRevApi.families({ q: q || undefined });
    setList(d);
  }, [q]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    drawingRevApi.unassigned({ limit: 1 }).then((d) => setUnassigned(d.total)).catch(() => {});
    drawingRevApi.fieldLabels().then(setMeta).catch(() => {});
    // 図番・最新版の書き換えは「編集」権限が要る（サーバー側も拒否する）。
    // 権限が無い人にボタンを見せると、押して403を見るまで分からない。
    dmsApi.meta().then((m) => setCanEdit(!!m.can_edit)).catch(() => setCanEdit(false));
  }, []);

  const openFamily = useCallback(async (key) => {
    setOpenKey(key); setFam(null); setSel([]);
    try { setFam(await drawingRevApi.family(key)); }
    catch { toast.error('この図番を開けませんでした'); setOpenKey(null); }
  }, []);

  const rescan = async () => {
    setBusy(true);
    try {
      const r = await drawingRevApi.scan({ only_missing: false });
      toast.success(`${r.scanned}枚を読み直しました（図番あり ${r.with_drawing_no}枚 / 図番なし ${r.without_drawing_no}枚 / ${r.reextracted || 0}枚を再抽出）`);
      await loadList();
      if (openKey) await openFamily(openKey);
      drawingRevApi.unassigned({ limit: 1 }).then((d) => setUnassigned(d.total)).catch(() => {});
    } catch { toast.error('読み直しに失敗しました'); }
    finally { setBusy(false); }
  };

  const confirm = async (documentId) => {
    try {
      setFam(await drawingRevApi.confirmLatest(openKey, documentId));
      toast.success('最新版を確定しました');
      loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || '確定できませんでした'); }
  };

  const unconfirm = async () => {
    try {
      setFam(await drawingRevApi.unconfirmLatest(openKey));
      toast.success('確定を取り消し、自動判定に戻しました');
      loadList();
    } catch { toast.error('取り消せませんでした'); }
  };

  const saveField = async (did, field, value) => {
    try {
      await drawingRevApi.patchRevision(did, { [field]: value });
      toast.success('直しました。以後の読み直しでも保持されます');
      await reloadFamily(openKey);
    } catch { toast.error('保存できませんでした'); }
  };

  const saveFieldValue = async (did, key, value) => {
    try {
      await drawingRevApi.patchFields(did, { [key]: value });
      setFam(await drawingRevApi.family(openKey));
      loadList();
      toast.success('直しました。以後の読み直しでも保持されます');
    } catch (e) { toast.error(e?.response?.data?.detail || '保存できませんでした'); }
  };

  const toggleSel = (did) => setSel((s) =>
    s.includes(did) ? s.filter((x) => x !== did) : [...s, did].slice(-2));

  const reloadFamily = async (key) => {
    try { setFam(await drawingRevApi.family(key)); }
    catch { setOpenKey(null); setFam(null); }
    loadList();
  };

  const splitOut = async (documentIds) => {
    try {
      const r = await drawingRevApi.splitOut(openKey, documentIds);
      toast.success(`${r.moved.length}枚を別図面として分けました（自動判定に戻せます）`);
      await reloadFamily(openKey);
    } catch (e) { toast.error(e?.response?.data?.detail || '分けられませんでした'); }
  };

  const setRule = async (rule) => {
    try {
      await drawingRevApi.setSuffixRule(openKey, rule);
      toast.success(rule === 'variant'
        ? '末尾の記号を別図番として扱います（読み直しても束ね直しません）'
        : '末尾の記号を版として束ねます');
      if (rule === 'variant') { setOpenKey(null); setFam(null); loadList(); }
      else await reloadFamily(openKey);
    } catch (e) { toast.error(e?.response?.data?.detail || '変更できませんでした'); }
  };

  const moveFamily = async () => {
    const target = (moveFor?.value || '').trim();
    if (!target) return;
    try {
      await drawingRevApi.moveFamily(moveFor.document_id, target);
      toast.success(`${target} の束へ移しました`);
      setMoveFor(null);
      await reloadFamily(openKey);
    } catch (e) { toast.error(e?.response?.data?.detail || '移せませんでした'); }
  };

  const clearOverride = async (did) => {
    try {
      await drawingRevApi.clearFamilyOverride(did);
      toast.success('自動判定に戻しました');
      await reloadFamily(openKey);
    } catch { toast.error('戻せませんでした'); }
  };

  const startCompare = () => {
    const members = sel.map((id) => fam.members.find((m) => m.document_id === id)).filter(Boolean);
    if (members.length !== 2) return;
    const sorted = [...members].sort((x, y) => (x.rev_date || '').localeCompare(y.rev_date || ''));
    setCompare({ a: { ...sorted[0], drawing_no: fam.drawing_no }, b: { ...sorted[1], drawing_no: fam.drawing_no } });
  };

  return (
    <div className="p-4 space-y-3" data-testid="drawing-revisions-page">
      <div className="flex items-start gap-3">
        <div>
          <h1 className="text-lg font-bold tracking-tight text-zinc-900">図面の改訂管理</h1>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            図面に書かれた図番・リビジョン・改訂日を読み取り、同じ図番の版をまとめて最新版を判定します。
          </p>
        </div>
        <button type="button" onClick={rescan} disabled={busy || !canEdit} data-testid="drawing-rev-rescan"
          title={canEdit ? '' : '図面の読み直しには「編集」権限が必要です'}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-teal-700 hover:bg-teal-800 text-white text-[11px] font-semibold disabled:opacity-50">
          <ArrowClockwise size={13} className={busy ? 'animate-spin' : ''} />
          {busy ? '読み取り中…' : '図面を読み直す'}
        </button>
      </div>

      {unassigned > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border-l-4 border-amber-500 text-[11px] text-amber-900"
          data-testid="drawing-rev-unassigned-note">
          <Warning size={14} weight="duotone" className="text-amber-600" />
          図番が読み取れなかった図面が <strong className="font-mono">{unassigned}</strong> 枚あります。
          これらは版としてまとめられません（図番を手で入れると束ねられます）。
        </div>
      )}

      {/* U3 — PDFの分割は スキャナー受信箱 の1箇所だけに置く。ここは案内のみ。 */}
      {!openKey && canEdit && (
        <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2.5 flex flex-wrap items-center gap-2"
          data-testid="dms-rev-split-link">
          <span className="text-xs text-zinc-600">
            まとめてスキャンしたPDFをページごとに分けるには、スキャナー受信箱をお使いください
          </span>
          <a href="/documents?view=scan" data-testid="dms-rev-split-goto"
            className="text-xs font-semibold text-teal-700 underline">スキャナー受信箱を開く</a>
        </div>
      )}

      {!openKey && (
        <>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="図番・名称・材質で絞り込む…"
            data-testid="drawing-rev-search"
            className="w-72 border border-zinc-300 rounded px-2.5 py-1.5 text-[12px] outline-none focus:border-teal-500" />
          <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
            {list === null && (
              <div className="py-16 text-center text-zinc-400" data-testid="drawing-rev-loading">
                <ArrowClockwise size={26} className="mx-auto mb-2 animate-spin" />
                <p className="text-[11px]">読み込んでいます…</p>
              </div>
            )}
            {list && list.families.length === 0 && (
              <div className="py-16 text-center text-zinc-400" data-testid="drawing-rev-empty">
                <Folders size={34} weight="duotone" className="mx-auto mb-2" />
                <p className="text-xs">まとめられる図番がまだありません</p>
                <p className="text-[11px] mt-1">「図面を読み直す」を押すと、図面から図番を読み取ります。</p>
              </div>
            )}
            {list?.families.map((f) => <FamilyRow key={f.drawing_no_key} f={f} onOpen={openFamily} />)}
          </div>
          {list && <p className="text-[10px] text-zinc-400">{list.total}件の図番</p>}
        </>
      )}

      {openKey && (
        <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
          <div className="flex items-center gap-3 px-3 py-2.5 border-b border-zinc-200 bg-zinc-50">
            <button type="button" onClick={() => { setOpenKey(null); setFam(null); }}
              data-testid="drawing-rev-back"
              className="text-[11px] text-zinc-500 hover:text-zinc-900">← 一覧へ</button>
            <span className="font-mono text-[13px] font-bold text-zinc-900">{fam?.drawing_no || openKey}</span>
            {fam && (
              <span className="text-[11px] text-zinc-500" data-testid="family-header-count">
                {fam.version_count ?? fam.members.length}版
                {fam.duplicate_count > 0 && (
                  <span className="text-orange-700"> ＋ 重複 {fam.duplicate_count}件</span>
                )}
              </span>
            )}
            {fam?.is_confirmed && canEdit && (
              <button type="button" onClick={unconfirm} data-testid="drawing-rev-unconfirm"
                className="ml-auto text-[11px] text-zinc-500 hover:text-zinc-900 underline">確定を取り消す</button>
            )}
            <button type="button" onClick={startCompare} disabled={sel.length !== 2}
              data-testid="drawing-rev-compare"
              className={`${fam?.is_confirmed ? '' : 'ml-auto'} inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-semibold ${
                sel.length === 2 ? 'bg-teal-700 hover:bg-teal-800 text-white' : 'bg-zinc-100 text-zinc-400 cursor-not-allowed'}`}>
              <ArrowsLeftRight size={13} />
              {sel.length === 2 ? '2枚を比較する' : `比較する2枚を選ぶ（${sel.length}/2）`}
            </button>
            {canEdit && fam && (
              <span className="inline-flex items-center gap-1 text-[10px] text-zinc-500"
                data-testid="drawing-rev-suffix-rule">
                末尾の記号:
                <button type="button" onClick={() => setRule('rev')}
                  data-testid="suffix-rule-rev"
                  className={`px-1.5 py-0.5 rounded border ${fam.suffix_rule !== 'variant'
                    ? 'bg-teal-700 text-white border-teal-700' : 'border-zinc-300 text-zinc-600'}`}>版</button>
                <button type="button" onClick={() => setRule('variant')}
                  data-testid="suffix-rule-variant"
                  title="末尾の記号を別図番として扱い、束を図番ごとに分けます（読み直しても束ね直しません）"
                  className={`px-1.5 py-0.5 rounded border ${fam.suffix_rule === 'variant'
                    ? 'bg-sky-700 text-white border-sky-700' : 'border-zinc-300 text-zinc-600'}`}>別図番</button>
              </span>
            )}
            {canEdit && sel.length > 0 && (
              <button type="button" onClick={() => splitOut(sel)}
                data-testid="drawing-rev-split-selected"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-zinc-300 text-[10px] text-zinc-600 hover:border-sky-500 hover:text-sky-700">
                <Scissors size={11} />選んだ{sel.length}枚を別図面にする
              </button>
            )}
          </div>

          {fam?.latest_undecided && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900"
              data-testid="drawing-rev-undecided">
              <Warning size={14} weight="duotone" className="text-amber-600" />
              最新版を判定できません（{fam.undecided_reason}）。REV か改訂日を入れるか、
              「最新版にする」で人が決めてください。
            </div>
          )}

          {fam?.duplicate_count > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border-b border-orange-200 text-[11px] text-orange-900"
              data-testid="drawing-rev-dups">
              <Files size={14} weight="duotone" className="text-orange-600" />
              同じ中身の図面が {fam.duplicate_count}件 あります（版としては数えていません）。
              1枚だけ残す場合は、重複の行から書類を開いて移動または削除してください。
            </div>
          )}

          {fam?.disagrees_with_auto && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900">
              <Warning size={14} weight="duotone" className="text-amber-600" />
              人が確定した版と、改訂日から自動判定した版が違います。どちらが正しいか確認してください。
            </div>
          )}

          {!fam && (
            <div className="py-14 text-center text-zinc-400">
              <ArrowClockwise size={24} className="mx-auto mb-2 animate-spin" />
              <p className="text-[11px]">版を読み込んでいます…</p>
            </div>
          )}

          {fam?.members.map((m, i) => (
            <div key={m.document_id}
              style={{ animationDelay: `${i * 40}ms` }}
              className={`border-b border-zinc-200 animate-in fade-in slide-in-from-bottom-1 ${
                m.is_latest ? 'bg-red-50/40' : 'hover:bg-zinc-50'} transition-colors`}>
            <div data-testid={`rev-row-${m.rev || 'none'}`}
              className="flex items-center gap-3 px-3 py-2.5">
              <input type="checkbox" checked={sel.includes(m.document_id)}
                onChange={() => toggleSel(m.document_id)}
                data-testid={`rev-select-${m.rev || 'none'}`}
                className="accent-teal-700" />
              <RevThumb documentId={m.document_id} onOpen={() => setPreview(m)} />
              <span className="w-44 shrink-0">
                <EditableField value={m.drawing_no} level={(m.confidence || {}).drawing_no}
                  placeholder="図番未読" testid={`dwgno-${m.document_id}`} canEdit={canEdit}
                  onSave={(v) => saveField(m.document_id, 'drawing_no', v)} />
              </span>
              <span className="w-40 shrink-0">
                <EditableField value={m.rev} level={(m.confidence || {}).rev} placeholder="REV未読"
                  testid={`rev-${m.document_id}`} canEdit={canEdit}
                  onSave={(v) => saveField(m.document_id, 'rev', v)} />
              </span>
              <span className="w-56 shrink-0">
                <EditableField value={m.rev_date} level={(m.confidence || {}).rev_date}
                  placeholder="改訂日未読" testid={`date-${m.document_id}`} canEdit={canEdit}
                  onSave={(v) => saveField(m.document_id, 'rev_date', v)} />
              </span>
              <span className="text-[11px] text-zinc-600 truncate flex-1" title={m.filename}>
                {m.title || m.filename}
              </span>
              {m.customer_drawing_no && (
                <span className="font-mono text-[10px] text-zinc-500 truncate max-w-[9rem]"
                  title="客先図番（束ねる鍵には使いません）"
                  data-testid={`rev-customer-${m.document_id}`}>客先 {m.customer_drawing_no}</span>
              )}
              {m.duplicate_of && (
                <span className="px-1.5 py-0.5 rounded bg-orange-50 border border-orange-300 text-orange-800 text-[9px] font-medium"
                  title="同じ中身の図面です（版ではありません）"
                  data-testid={`rev-dup-${m.document_id}`}>重複</span>
              )}
              {m.family_key_manual && (
                <span className="px-1.5 py-0.5 rounded bg-sky-50 border border-sky-200 text-sky-700 text-[9px]"
                  title={`${m.family_manual_by || '人'} が ${(m.family_manual_at || '').slice(0, 10)} に分けました`}
                  data-testid={`rev-manual-${m.document_id}`}>人が分けた</span>
              )}
              <a href={`/documents?open=${m.document_id}`} target="_blank" rel="noopener noreferrer"
                data-testid={`rev-docno-${m.document_id}`}
                className="font-mono text-[10px] text-teal-700 hover:underline w-24 truncate inline-flex items-center gap-0.5">
                {m.document_number}<ArrowSquareOut size={9} />
              </a>
              {m.is_latest ? (
                <span className="px-2 py-0.5 rounded bg-red-600 text-white text-[10px] font-bold tracking-wider shadow-sm"
                  data-testid="rev-latest-badge">最新版</span>
              ) : canEdit ? (
                <button type="button" onClick={() => confirm(m.document_id)}
                  data-testid={`rev-confirm-${m.rev || 'none'}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-zinc-300 text-[10px] text-zinc-600 hover:border-teal-500 hover:text-teal-700">
                  <CheckSquareOffset size={11} />最新版にする
                </button>
              ) : null}
              <button type="button" data-testid={`rev-similar-${m.document_id}`}
                onClick={() => setSimilarFor(similarFor === m.document_id ? null : m.document_id)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] ${
                  similarFor === m.document_id
                    ? 'border-teal-500 text-teal-700 bg-teal-50'
                    : 'border-zinc-300 text-zinc-600 hover:border-teal-500 hover:text-teal-700'}`}>
                <Copy size={11} />類似図面
              </button>
              {canEdit && (m.family_key_manual ? (
                <button type="button" onClick={() => clearOverride(m.document_id)}
                  data-testid={`rev-auto-${m.document_id}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-zinc-300 text-[10px] text-zinc-600 hover:border-teal-500 hover:text-teal-700">
                  <ArrowUUpLeft size={11} />自動判定に戻す
                </button>
              ) : (
                <button type="button" onClick={() => splitOut([m.document_id])}
                  data-testid={`rev-split-${m.document_id}`}
                  title="この図面はこの束の版ではありません（図番そのままで独立した束にします）"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-zinc-300 text-[10px] text-zinc-600 hover:border-sky-500 hover:text-sky-700">
                  <Scissors size={11} />版ではない
                </button>
              ))}
              {canEdit && (
                <button type="button"
                  onClick={() => setMoveFor({ document_id: m.document_id, value: '' })}
                  data-testid={`rev-move-${m.document_id}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-zinc-300 text-[10px] text-zinc-600 hover:border-sky-500 hover:text-sky-700">
                  別の図番に移す
                </button>
              )}
            </div>
            <FieldsBar member={m} labels={meta.labels} order={meta.order} onSave={saveFieldValue}
              canEdit={canEdit} />
            {similarFor === m.document_id && (
              <SimilarDrawings member={{ ...m, drawing_no: m.drawing_no || fam.drawing_no }}
                labels={meta.labels} onClose={() => setSimilarFor(null)}
                onCompare={(it) => setCompare({
                  a: { ...m, drawing_no: m.drawing_no || fam.drawing_no },
                  b: { document_id: it.document_id, drawing_no: it.drawing_no,
                       title: it.title, rev: it.rev },
                })} />
            )}
            </div>
          ))}

          {fam && (
            <p className="px-3 py-2 text-[10px] text-zinc-400">
              {fam.is_confirmed
                ? `${fam.confirmed_by} が確定（${(fam.confirmed_at || '').slice(0, 10)}）`
                : '改訂日とリビジョン記号から自動で判定しています。確定すると、この判定より人の選択が優先されます。'}
            </p>
          )}
        </div>
      )}

      {compare && (
        <DrawingCompare a={compare.a} b={compare.b} onClose={() => setCompare(null)}
          onSwap={() => setCompare((c) => ({ a: c.b, b: c.a }))} />
      )}

      {preview && (
        <DocFileViewer documentId={preview.document_id} filename={preview.filename}
          title={preview.title || preview.filename} mime={preview.mime_type}
          onClose={() => setPreview(null)} />
      )}

      {moveFor && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4"
          data-testid="rev-move-dialog" onClick={() => setMoveFor(null)}>
          <div className="w-full max-w-md rounded-lg bg-white p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}>
            <h2 className="text-base font-bold text-zinc-900">別の図番に移す</h2>
            <p className="text-[11px] text-zinc-500">
              書類・DOC番号・フォルダーは動きません。版としてまとめる図番だけを変えます。
            </p>
            <input list="rev-family-keys" autoFocus value={moveFor.value}
              onChange={(e) => setMoveFor({ ...moveFor, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); moveFamily(); }
                if (e.key === 'Escape') { e.preventDefault(); setMoveFor(null); }
              }}
              placeholder="移す先の図番（例 HT-4202-B）" data-testid="rev-move-input"
              className="w-full border border-zinc-300 rounded px-2 py-1.5 font-mono text-[12px] outline-none focus:border-teal-500" />
            <datalist id="rev-family-keys">
              {(list?.families || []).map((f) => (
                <option key={f.drawing_no_key} value={f.drawing_no_key} />
              ))}
            </datalist>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setMoveFor(null)} data-testid="rev-move-cancel"
                className="px-3 py-1.5 rounded border border-zinc-300 text-[11px] text-zinc-600">やめる</button>
              <button type="button" onClick={moveFamily} data-testid="rev-move-confirm"
                disabled={!moveFor.value.trim()}
                className="px-3 py-1.5 rounded bg-teal-700 text-white text-[11px] font-semibold disabled:opacity-40">移す</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
